import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight, Sparkles, CheckCircle2, MessageSquareHeart, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { sportIcon, intensityClass } from "../lib/workout";
import CheckinDialog from "../components/CheckinDialog";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function Today() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/today");
    setData(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const onCheckinDone = (res) => {
    load();
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
      </div>
    );
  }

  const w = data.workout;
  const checkedIn = !!data.checkin;
  const Icon = sportIcon(w?.sport);

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <p className="text-muted-foreground text-sm font-medium">{greeting()},</p>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-foreground" data-testid="today-greeting">
          {data.athlete_name || "Athlete"}
        </h1>
      </motion.div>

      {!w && (
        <div className="bg-card border border-border rounded-2xl p-8 text-center" data-testid="today-empty">
          {data.next_workout ? (
            <>
              <p className="font-heading font-bold text-lg text-foreground">Your plan starts soon</p>
              <p className="text-muted-foreground mt-1">
                Rest up — your first session (<span className="text-foreground font-medium">{data.next_workout.title}</span>) is on {data.next_workout.date}.
              </p>
              <Button data-testid="today-open-next" onClick={() => navigate(`/workout/${data.next_workout.id}`)} className="mt-5 h-11 px-6 rounded-xl font-semibold">
                Preview it <ArrowRight size={16} className="ml-1" />
              </Button>
            </>
          ) : (
            <p className="text-muted-foreground">No workout scheduled for today.</p>
          )}
        </div>
      )}

      {w && (
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="relative overflow-hidden rounded-2xl border border-border bg-card"
          data-testid="today-workout-card"
        >
          {w.adapted && (
            <div className="flex items-center gap-1.5 text-xs font-semibold bg-brand-orange/15 text-brand-orange px-4 py-2" data-testid="today-adapted-badge">
              <RefreshCw size={13} /> Adapted for today
            </div>
          )}
          <div className="p-6 sm:p-8">
            <div className="flex items-center justify-between mb-5">
              <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${intensityClass(w.intensity)}`}>
                {w.intensity}
              </span>
              <div className="flex items-center gap-1.5 text-muted-foreground text-sm font-medium">
                <Clock size={15} /> {w.duration_min} min
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="brand-gradient w-14 h-14 rounded-2xl flex items-center justify-center shrink-0">
                <Icon className="text-white" size={26} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{w.sport}</p>
                <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-foreground mt-0.5" data-testid="today-workout-title">
                  {w.title}
                </h2>
              </div>
            </div>

            {w.coach_message ? (
              <div className="mt-6 flex items-start gap-3 bg-muted/60 rounded-xl p-4" data-testid="today-coach-message">
                <div className="brand-gradient w-8 h-8 rounded-lg flex items-center justify-center font-heading font-black text-white text-sm shrink-0">C</div>
                <p className="text-foreground leading-relaxed text-[15px]">{w.coach_message}</p>
              </div>
            ) : (
              <div className="mt-6">
                <p className="text-xs font-bold uppercase tracking-widest text-brand-orange mb-1.5">Why this session</p>
                <p className="text-foreground leading-relaxed" data-testid="today-workout-why">{w.purpose}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 mt-7">
              {w.status === "planned" ? (
                <>
                  {!w.is_rest && (
                    <Button
                      data-testid="today-view-workout"
                      onClick={() => navigate(`/workout/${w.id}`)}
                      className="flex-1 h-12 rounded-xl font-semibold active:scale-95 transition-transform"
                    >
                      Open workout <ArrowRight size={18} className="ml-1" />
                    </Button>
                  )}
                  <Button
                    variant={w.is_rest ? "default" : "outline"}
                    data-testid="today-checkin-btn"
                    onClick={() => setCheckinOpen(true)}
                    className="flex-1 h-12 rounded-xl font-semibold active:scale-95 transition-transform"
                  >
                    {checkedIn ? "Re-check in" : "Daily check-in"}
                  </Button>
                </>
              ) : (
                <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-semibold" data-testid="today-status">
                  <CheckCircle2 size={20} /> {w.status === "completed" ? "Completed — nice work" : "Marked as skipped"}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Check-in nudge */}
      {w && w.status === "planned" && (
        <motion.button
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onClick={() => setCheckinOpen(true)}
          data-testid="today-readiness-card"
          className="w-full text-left rounded-2xl border border-border bg-card p-6 hover:border-primary/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-brand-yellow/15 flex items-center justify-center shrink-0">
              <MessageSquareHeart className="text-brand-yellow" size={22} />
            </div>
            <div>
              <p className="font-heading font-bold text-lg text-foreground">
                {checkedIn ? "Something changed?" : "How are you feeling?"}
              </p>
              <p className="text-sm text-muted-foreground">
                {checkedIn ? "Check in again and I'll re-decide." : "Tell me your sleep and energy — I'll adapt today's session."}
              </p>
            </div>
          </div>
        </motion.button>
      )}

      {/* Coach summary */}
      {data.coach_summary && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="rounded-2xl border border-border bg-card p-6"
          data-testid="today-coach-summary"
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="text-brand-orange" size={16} />
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Your coach's read</p>
          </div>
          <p className="text-foreground leading-relaxed text-[15px]">{data.coach_summary}</p>
        </motion.div>
      )}

      <CheckinDialog open={checkinOpen} onOpenChange={setCheckinOpen} onDone={onCheckinDone} />
    </div>
  );
}
